// @ts-check

/**
 * @file Theme-aware label + Select for filter rows that live outside an
 * api-maker Form.
 */

import Text from "@kaspernj/api-maker/build/utils/text"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native"

import Select from "./select"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {{text: string, value: string}} LabeledSelectOption */

/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} LabeledSelectProps
 * @property {string} [label] Field label rendered above the select.
 * @property {boolean} [multiple] Allow multiple selections.
 * @property {(...args: unknown[]) => unknown} [onChangeValue] Selection change handler.
 * @property {LabeledSelectOption[]} options Available options.
 * @property {string} [placeholder] Placeholder text.
 * @property {boolean} [search] Whether the select supports searching.
 * @property {string} testID Stable test id.
 * @property {(string | number)[]} [values] Selected values for controlled use.
 */

/** @typedef {Record<string, never>} LabeledSelectState */

/**
 * Labeled select — uppercase muted label + theme-aware shared `Select`.
 * Use for filter rows where the form-bound `FormSelect` doesn't fit.
 * @augments {ShapeComponent<LabeledSelectProps, LabeledSelectState>}
 */
class LabeledSelect extends ShapeComponent {
  static propTypes = propTypesExact({
    label: PropTypes.string,
    multiple: PropTypes.bool,
    onChangeValue: PropTypes.func,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    ).isRequired,
    placeholder: PropTypes.string,
    search: PropTypes.bool,
    testID: PropTypes.string.isRequired,
    values: PropTypes.array
  })
  static defaultProps = {
    label: undefined,
    multiple: false,
    onChangeValue: undefined,
    placeholder: undefined,
    search: false,
    values: undefined
  }

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active theme palette into the instance. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the labeled select.
   * @returns {ReactNode} Labeled select content.
   */
  render() {
    const {label, multiple, onChangeValue, options, placeholder, search, testID, values} = this.p
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View
        style={styles.container ||= {
          flex: 1,
          minWidth: 0,
          width: "100%"
        }}
      >
        {label != null &&
          <Text
            style={styles[`label-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.5,
              marginBottom: 6,
              textTransform: "uppercase"
            }}
            testID={`${testID}/label`}
          >
            {label}
          </Text>
        }
        <Select
          multiple={multiple}
          onChangeValue={onChangeValue}
          options={options}
          placeholder={placeholder}
          search={search}
          testID={testID}
          values={values}
        />
      </View>
    )
  }
}

/** @type {NamedExoticComponent<LabeledSelectProps>} */
const LabeledSelectComponent = memo(shapeComponent(LabeledSelect))

export default LabeledSelectComponent
